/**
 * Reading errors out of `ApiService`.
 *
 * `ApiService` rejects with the API's **response body**, not the axios error
 * (`throw error?.response?.data || error`), and that body is shaped
 * `{ status, message, ...extraFields }` by the backend's exception filter — a Zod
 * refusal puts one formatted issue per entry in `message` as an array. Only when
 * there is no body at all (a network failure, an aborted request) does the original
 * error come through, so both shapes have to be handled.
 *
 * Reading `error.response.status` — the axios shape — matches nothing here, which is
 * how a 409 or a 403 ends up presented as a generic failure.
 */

interface ApiErrorBody {
  status?: number;
  statusCode?: number;
  message?: unknown;
  [key: string]: unknown;
}

/** The API's response body for a failed request, however it was thrown. */
export const apiErrorBody = (error: unknown): ApiErrorBody => {
  const fromAxios = (error as { response?: { data?: unknown } })?.response?.data;
  if (fromAxios && typeof fromAxios === 'object') return fromAxios as ApiErrorBody;
  if (error && typeof error === 'object') return error as ApiErrorBody;
  return {};
};

/** HTTP status of a failed request, or undefined when it never reached the API. */
export const apiErrorStatus = (error: unknown): number | undefined => {
  const body = apiErrorBody(error);
  return (
    body.status ??
    body.statusCode ??
    (error as { response?: { status?: number } })?.response?.status
  );
};

/**
 * Messages that explain nothing to the person reading the form: ApiService's own
 * placeholder, and the transport failures that mean "the request never landed" —
 * the caller's own wording for that is kinder than axios's.
 */
const UNHELPFUL_MESSAGES = new Set(['API request failed.', 'Network Error', 'canceled']);

const isUnhelpful = (message: string): boolean =>
  UNHELPFUL_MESSAGES.has(message) ||
  message.startsWith('timeout of ') ||
  message.startsWith('Request failed with status code');

/**
 * The message the API actually sent, or null when it sent nothing usable — a Zod
 * refusal's issues are joined so the form can name every field it rejected.
 */
export const apiErrorMessage = (error: unknown): string | null => {
  const { message } = apiErrorBody(error);

  if (Array.isArray(message)) {
    const issues = message.filter((entry): entry is string => typeof entry === 'string');
    if (issues.length > 0) return issues.join(' ');
  }
  if (typeof message === 'string' && message.trim() && !isUnhelpful(message)) {
    return message;
  }

  // ApiService also throws a plain Error for a 2xx body that carries
  // `success: false`, and there the Error's message is the server's own wording.
  const thrown = error instanceof Error ? error.message.trim() : '';
  return thrown && !isUnhelpful(thrown) ? thrown : null;
};
